import React, { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  XMarkIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import AutoComplete from "../AutoComplete";

export default function Grid({
  title = "Data",
  headerAttributes = [],
  fetchDataFunction,
  mapDataFunction,
  loadingSelector = (state) => state.loaderSpinner?.loading,
  rowsPerPage = 50,
  defaultSortBy = "id",
  defaultSortOrder = "desc",
  showFilters = true,
  showDateFilter = true,
  showPagination = true,
  customFilters = [],
  searchDebounceMs = 1500,
}) {
  const [dataList, setDataList] = useState([]);
  const [rawResults, setRawResults] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortBy, setSortBy] = useState(defaultSortBy);
  const [sortOrder, setSortOrder] = useState(defaultSortOrder);
  const [searchFilters, setSearchFilters] = useState({});
  const [debouncedSearchFilters, setDebouncedSearchFilters] = useState({});
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const [multiSelectFilters, setMultiSelectFilters] = useState({});
  const [dropdownOpen, setDropdownOpen] = useState({});
  const [filterLoadingStates, setFilterLoadingStates] = useState({});

  // Redux
  const dispatch = useDispatch();
  const loading = useSelector(loadingSelector);

  // Track filter loading states with 5-second timeout
  useEffect(() => {
    const timers = {};

    customFilters.forEach((filter) => {
      if (filter.type === "autocomplete-multiselect") {
        if (!filter.options || filter.options.length === 0) {
          // Set loading state
          setFilterLoadingStates((prev) => ({ ...prev, [filter.id]: true }));

          // Set timeout to stop loading after 5 seconds
          timers[filter.id] = setTimeout(() => {
            setFilterLoadingStates((prev) => ({ ...prev, [filter.id]: false }));
          }, 5000);
        } else {
          // Has options, not loading
          setFilterLoadingStates((prev) => ({ ...prev, [filter.id]: false }));
        }
      }
    });

    // Cleanup function
    return () => {
      Object.values(timers).forEach((timer) => clearTimeout(timer));
    };
  }, [customFilters]);

  // Debounce search filters
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchFilters(searchFilters);
      setCurrentPage(0); // Reset to first page when search changes
    }, searchDebounceMs);

    return () => clearTimeout(timer);
  }, [searchFilters, searchDebounceMs]);

  const fetchData = useCallback(
    async (page = currentPage) => {
      if (!fetchDataFunction) return;

      const params = {
        page: page + 1,
        sortBy,
        sortOrder,
        ...debouncedSearchFilters,
        ...multiSelectFilters,
        from: dateRange.from || null,
        to: dateRange.to || null,
      };

      const result = await fetchDataFunction(dispatch, params);

      if (result && !result.error) {
        setTotalRecords(result.count || 0);
        if (result.results) {
          setRawResults(result.results);
        }
      }
    },
    [
      dispatch,
      currentPage,
      sortBy,
      sortOrder,
      debouncedSearchFilters,
      dateRange,
      multiSelectFilters,
      fetchDataFunction,
    ]
  );

  // Separate effect to re-map data when mapDataFunction or rawResults change
  useEffect(() => {
    if (rawResults && mapDataFunction) {
      setDataList(mapDataFunction(rawResults));
    }
  }, [rawResults, mapDataFunction]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSort = (columnId) => {
    const header = headerAttributes.find((h) => h.id === columnId);
    if (!header?.sortable) return;

    if (sortBy === columnId) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(columnId);
      setSortOrder("desc");
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleSearch = (columnId, value) => {
    setSearchFilters((prev) => ({
      ...prev,
      [columnId]: value,
    }));
    // Page reset is now handled by the debounce effect
  };

  const handleDateChange = (field, value) => {
    setDateRange((prev) => ({
      ...prev,
      [field]: value,
    }));
    setCurrentPage(0); // Reset to first page on date change
  };

  // Multi-select filter handlers
  const handleMultiSelectToggle = (filterId, option) => {
    setMultiSelectFilters((prev) => {
      const current = prev[filterId] || [];
      const isSelected = current.includes(option.value);

      let newValues;
      if (isSelected) {
        newValues = current.filter((val) => val !== option.value);
      } else {
        newValues = [...current, option.value];
      }

      return {
        ...prev,
        [filterId]: newValues.length > 0 ? newValues.join(",") : undefined,
      };
    });
    setCurrentPage(0);
  };

  const handleMultiSelectRemove = (filterId, valueToRemove) => {
    setMultiSelectFilters((prev) => {
      const current = prev[filterId] ? prev[filterId].split(",") : [];
      const newValues = current.filter((val) => val !== valueToRemove);

      return {
        ...prev,
        [filterId]: newValues.length > 0 ? newValues.join(",") : undefined,
      };
    });
    setCurrentPage(0);
  };

  // Autocomplete multi-select handlers
  const handleAutocompleteAdd = (filterId, option) => {
    if (!option?.value || !option?.label) return;

    setMultiSelectFilters((prev) => {
      const current = prev[filterId] ? prev[filterId].split(",") : [];
      if (current.includes(option.value)) return prev;

      const newValues = [...current, option.value];
      return {
        ...prev,
        [filterId]: newValues.join(","),
      };
    });
    setCurrentPage(0);
  };

  const handleAutocompleteRemove = (filterId, valueToRemove) => {
    setMultiSelectFilters((prev) => {
      const current = prev[filterId] ? prev[filterId].split(",") : [];
      const newValues = current.filter((val) => val !== valueToRemove);

      return {
        ...prev,
        [filterId]: newValues.length > 0 ? newValues.join(",") : undefined,
      };
    });
    setCurrentPage(0);
  };

  const getSelectedOptions = (filterId, options) => {
    const selectedValues = multiSelectFilters[filterId]
      ? multiSelectFilters[filterId].split(",")
      : [];
    return options.filter((option) => selectedValues.includes(option.value));
  };

  // Get available options for autocomplete (excluding already selected)
  const getAvailableOptions = (filterId, options) => {
    const selectedValues = multiSelectFilters[filterId]
      ? multiSelectFilters[filterId].split(",")
      : [];
    return options.filter((option) => !selectedValues.includes(option.value));
  };

  // Label function for autocomplete
  const labelFunction = useCallback((option) => option?.label || "", []);

  const totalPages = Math.ceil(totalRecords / rowsPerPage);
  const startRecord = currentPage * rowsPerPage + 1;
  const endRecord = Math.min((currentPage + 1) * rowsPerPage, totalRecords);

  const searchableHeaders = headerAttributes.filter((attr) => attr.searchable);

  return (
    <div className="bg-white shadow-lg rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          {showFilters && (
            <button
              onClick={() => setShowFiltersPanel(!showFiltersPanel)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
              {showFiltersPanel ? "Hide Filters" : "Show Filters"}
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      {showFilters && showFiltersPanel && (
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {/* Date Range */}
            {showDateFilter && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    From Date
                  </label>
                  <input
                    type="date"
                    value={dateRange.from}
                    onChange={(e) => handleDateChange("from", e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    To Date
                  </label>
                  <input
                    type="date"
                    value={dateRange.to}
                    onChange={(e) => handleDateChange("to", e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </>
            )}

            {/* Search Filters */}
            {searchableHeaders.slice(0, showDateFilter ? 4 : 4).map((attr) => {
              // Handle datetime-range type
              if (attr.searchable.type === "datetime-range") {
                return (
                  <React.Fragment key={attr.id}>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        {attr.searchable.fromLabel || "From Date & Time"}
                      </label>
                      <input
                        type={attr.searchable.inputType || "datetime-local"}
                        value={searchFilters[`${attr.id}_from`] || ""}
                        onChange={(e) =>
                          handleSearch(`${attr.id}_from`, e.target.value)
                        }
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        {attr.searchable.toLabel || "To Date & Time"}
                      </label>
                      <input
                        type={attr.searchable.inputType || "datetime-local"}
                        value={searchFilters[`${attr.id}_to`] || ""}
                        onChange={(e) =>
                          handleSearch(`${attr.id}_to`, e.target.value)
                        }
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                  </React.Fragment>
                );
              }

              // Handle regular text search
              return (
                <div key={attr.id}>
                  <label className="block text-sm font-medium text-gray-700">
                    {attr.searchable.label}
                  </label>
                  <input
                    type="text"
                    value={searchFilters[attr.id] || ""}
                    onChange={(e) => handleSearch(attr.id, e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder={`Search ${attr.label.toLowerCase()}...`}
                  />
                </div>
              );
            })}

            {/* Custom Filters */}
            {customFilters.map((filter) => (
              <div key={filter.id} className="relative">
                <label className="block text-sm font-medium text-gray-700">
                  {filter.label}
                </label>

                {filter.type === "autocomplete-multiselect" ? (
                  // Autocomplete Multi-Select Filter
                  <div>
                    {filterLoadingStates[filter.id] ? (
                      // Show loading state
                      <div className="mt-1 relative w-full bg-white border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left text-gray-500 sm:text-sm">
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                          Loading options...
                        </div>
                      </div>
                    ) : !filter.options || filter.options.length === 0 ? (
                      // Show no options available
                      <div className="mt-1 relative w-full bg-gray-50 border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left text-gray-400 sm:text-sm">
                        No options available
                      </div>
                    ) : (
                      // Show normal autocomplete
                      <AutoComplete
                        options={getAvailableOptions(
                          filter.id,
                          filter.options || []
                        )}
                        onSelect={(option) =>
                          handleAutocompleteAdd(filter.id, option)
                        }
                        labelFunction={labelFunction}
                        className="mt-1"
                        clearOnSelect
                        disabled={false}
                      />
                    )}
                    {/* Selected tags */}
                    {getSelectedOptions(filter.id, filter.options || [])
                      .length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {getSelectedOptions(
                          filter.id,
                          filter.options || []
                        ).map((option) => (
                          <Tag
                            key={option.value}
                            label={option.label}
                            onRemove={() =>
                              handleAutocompleteRemove(filter.id, option.value)
                            }
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  // Regular Multi-Select Filter (dropdown with checkboxes)
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() =>
                        setDropdownOpen((prev) => ({
                          ...prev,
                          [filter.id]: !prev[filter.id],
                        }))
                      }
                      className="mt-1 relative w-full bg-white border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <span className="block truncate">
                        {getSelectedOptions(filter.id, filter.options || [])
                          .length > 0
                          ? `${getSelectedOptions(filter.id, filter.options || []).length} selected`
                          : filter.placeholder || "Select options..."}
                      </span>
                      <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <ChevronDownIcon
                          className="h-5 w-5 text-gray-400"
                          aria-hidden="true"
                        />
                      </span>
                    </button>

                    {dropdownOpen[filter.id] && (
                      <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                        {(filter.options || []).map((option) => {
                          const isSelected = multiSelectFilters[filter.id]
                            ?.split(",")
                            .includes(option.value);
                          return (
                            <div
                              key={option.value}
                              onClick={() =>
                                handleMultiSelectToggle(filter.id, option)
                              }
                              className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-[var(--secondary-color)] ${
                                isSelected ? "bg-[var(--secondary-color)]" : ""
                              }`}
                            >
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => {}}
                                  className="h-4 w-4 text-[var(--primary-color)] focus:ring-[var(--primary-color)] border-gray-300 rounded"
                                />
                                <span className="ml-3 block truncate font-normal">
                                  {option.label}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Selected tags */}
                    {getSelectedOptions(filter.id, filter.options || [])
                      .length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {getSelectedOptions(
                          filter.id,
                          filter.options || []
                        ).map((option) => (
                          <span
                            key={option.value}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {option.label}
                            <button
                              type="button"
                              onClick={() =>
                                handleMultiSelectRemove(filter.id, option.value)
                              }
                              className="flex-shrink-0 ml-1 h-4 w-4 rounded-full inline-flex items-center justify-center text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none focus:bg-blue-500 focus:text-white"
                            >
                              <XMarkIcon className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {headerAttributes
                .filter((header) => !header.hidden)
                .map((header) => (
                  <th
                    key={header.id}
                    style={{ width: header.width }}
                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                      header.sortable ? "cursor-pointer hover:bg-gray-100" : ""
                    }`}
                    onClick={() => header.sortable && handleSort(header.id)}
                    title={header.tooltip}
                  >
                    <div className="flex items-center space-x-1">
                      <span>{header.label}</span>
                      {header.sortable && (
                        <div className="flex flex-col">
                          <ArrowUpIcon
                            className={`h-3 w-3 ${
                              sortBy === header.id && sortOrder === "asc"
                                ? "text-blue-600"
                                : "text-gray-400"
                            }`}
                          />
                          <ArrowDownIcon
                            className={`h-3 w-3 -mt-1 ${
                              sortBy === header.id && sortOrder === "desc"
                                ? "text-blue-600"
                                : "text-gray-400"
                            }`}
                          />
                        </div>
                      )}
                    </div>
                  </th>
                ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading && dataList.length === 0 ? (
              <tr>
                <td
                  colSpan={headerAttributes.filter((h) => !h.hidden).length}
                  className="px-6 py-12 text-center"
                >
                  <div className="flex justify-center items-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-gray-500">Loading data...</span>
                  </div>
                </td>
              </tr>
            ) : dataList.length === 0 ? (
              <tr>
                <td
                  colSpan={headerAttributes.filter((h) => !h.hidden).length}
                  className="px-6 py-12 text-center text-gray-500"
                >
                  No records found
                </td>
              </tr>
            ) : (
              dataList.map((row, index) => (
                <tr
                  key={row.id}
                  className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                >
                  {headerAttributes
                    .filter((header) => !header.hidden)
                    .map((header) => (
                      <td
                        key={header.id}
                        className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                      >
                        {row[header.id]}
                      </td>
                    ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {showPagination && (
        <div className="bg-white px-6 py-3 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">{startRecord}</span> to{" "}
              <span className="font-medium">{endRecord}</span> of{" "}
              <span className="font-medium">{totalRecords}</span> results
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 0}
                className={`inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium ${
                  currentPage === 0
                    ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                    : "text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                }`}
              >
                <ChevronLeftIcon className="h-4 w-4 mr-1" />
                Previous
              </button>

              <span className="text-sm text-gray-700">
                Page {currentPage + 1} of {totalPages}
              </span>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages - 1}
                className={`inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium ${
                  currentPage >= totalPages - 1
                    ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                    : "text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                }`}
              >
                Next
                <ChevronRightIcon className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Reusable Tag component for selected items
function Tag({ label, onRemove }) {
  return (
    <div className="flex items-center bg-gray-200 text-gray-800 px-3 py-1 rounded-full text-sm">
      <span>{label}</span>
      <button
        onClick={onRemove}
        className="ml-2 text-gray-600 hover:text-red-500 focus:outline-none"
        title="Remove"
      >
        ×
      </button>
    </div>
  );
}
